import { Box, Tooltip, Typography } from '@mui/material'

type FormatValueOptions = {
  decimals?: number
  prefix?: string
}

/** Returns the maximum number of significant decimal places across raw string values. */
export function getMaxDecimals(values: (string | null)[]): number {
  let max = 0
  for (const v of values) {
    if (!v) continue
    const dot = v.indexOf('.')
    if (dot === -1) continue

    const fractionalPart = v.slice(dot + 1).replace(/0+$/, '')
    max = Math.max(max, fractionalPart.length)
  }
  return max
}

/** Formats a numeric string with optional fixed decimals and prefix (e.g. currency symbol). */
export function formatValue(
  value: string | null,
  { decimals, prefix = '' }: FormatValueOptions = {},
): string {
  if (!value) return '—'

  const parsed = parseFloat(value)
  if (isNaN(parsed)) return value

  if (decimals === undefined) {
    return `${prefix}${parsed.toLocaleString('en-US')}`
  }
  return `${prefix}${parsed.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })}`
}

type LineItemWithJob = {
  jobNumber: string | null
  phaseNumber: string | null
}

function getSharedLineItemNumber(
  rows: LineItemWithJob[],
  key: 'jobNumber' | 'phaseNumber',
): string | null | undefined {
  if (rows.length === 0) return null

  const normalizedNumbers = rows.map((row) => row[key]?.trim() || null)
  const firstNumber = normalizedNumbers[0]

  return normalizedNumbers.every((num) => num === firstNumber)
    ? firstNumber
    : undefined
}

export function getSharedLineItemJob(
  rows: LineItemWithJob[],
): string | null | undefined {
  return getSharedLineItemNumber(rows, 'jobNumber')
}

export function getSharedLineItemPhase(
  rows: LineItemWithJob[],
): string | null | undefined {
  return getSharedLineItemNumber(rows, 'phaseNumber')
}

export function FieldItem({
  label,
  value,
  mono,
  tooltip = '',
}: {
  label: string
  value: string | null
  mono?: boolean
  tooltip?: string
}) {
  return (
    <Tooltip
      title={tooltip}
      disableHoverListener={!tooltip}
      enterDelay={200}
      arrow
      placement="top"
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', lineHeight: 1.2, mb: 0.25 }}
        >
          {label}
        </Typography>
        <Typography
          variant="body2"
          fontWeight={500}
          noWrap
          sx={mono ? { fontFamily: 'monospace' } : undefined}
        >
          {value || '—'}
        </Typography>
      </Box>
    </Tooltip>
  )
}
